import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Table, Badge, Modal, Form, Alert, Toast, ToastContainer } from 'react-bootstrap';
import { FiTrash2, FiCalendar, FiFolder, FiEye } from 'react-icons/fi';
import { doc, collection } from 'firebase/firestore';
import { db, rtdb } from '../../firebase';
import { ref, set, onValue } from 'firebase/database';
import { useAuth } from '../../contexts/AuthContext';
import { firestoreHelpers, documentHelpers, clientHelpers } from '../../utils/firestoreHelpers';

const YearManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { client } = location.state || {};
  const { getClientDocRef, getClientYearsRef, getYearDocRef, getYearDocumentsRef, getUserClientPath } = useAuth();

  const [years, setYears] = useState([]);
  const [showAddYearModal, setShowAddYearModal] = useState(false);
  const [showEditYearModal, setShowEditYearModal] = useState(false);
  const [newYear, setNewYear] = useState("");
  const [editYear, setEditYear] = useState("");
  const [originalYear, setOriginalYear] = useState("");
  const [currentClient, setCurrentClient] = useState(client); // Live client data

  // Toast states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");

  // Loading states
  const [isLoadingYears, setIsLoadingYears] = useState(true);
  const [isLoadingDocumentCounts, setIsLoadingDocumentCounts] = useState(false);
  const [isAddingYear, setIsAddingYear] = useState(false);
  const [isEditingYear, setIsEditingYear] = useState(false);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteYearTarget, setDeleteYearTarget] = useState(null);
  const [deleteDocCount, setDeleteDocCount] = useState(0);

  // Helper function to format year as "2020-21"
  const formatYear = (year) => {
    const yearNum = parseInt(year);
    const nextYear = yearNum + 1;
    const nextYearShort = nextYear.toString().slice(-2);
    return `${yearNum}-${nextYearShort}`;
  };
  // Set up real-time listener for client data updates using Firestore
  useEffect(() => {
    if (!client?.id && !client?.contact) {
      setIsLoadingYears(false);
      return;
    }

    setIsLoadingYears(true);

    // Use client contact as document ID (client.id should be the contact number)
    const clientContact = client.contact || client.id;
    const clientName = client.name;
    console.log("🔄 Setting up Firestore listener for client:", clientName, "(Contact:", clientContact, ")");
    console.log("👤 Client data:", client);

    const clientDocRef = getClientDocRef(clientContact);
    if (!clientDocRef) {
      console.log("⚠️ No client document reference available for Contact:", clientContact);
      setIsLoadingYears(false);
      return;
    }

    const unsubscribe = firestoreHelpers.subscribe(
      clientDocRef,
      (updatedClient) => {
        if (updatedClient) {
          console.log("📊 Client data updated from Firestore:", updatedClient);
          setCurrentClient({ ...updatedClient, id: clientContact });

          // Get years from the client's years array
          const clientYears = updatedClient.years || [];

          // Sort years in descending order (handle both "2028-29" and "2028" formats)
          clientYears.sort((a, b) => {
            const getStartYear = (yearStr) => {
              if (yearStr.includes('-')) {
                return parseInt(yearStr.split('-')[0]);
              }
              return parseInt(yearStr);
            };
            return getStartYear(b) - getStartYear(a);
          });
          setYears(clientYears);
          console.log("📅 Years from Firestore:", clientYears);
        } else {
          console.log("❌ Client not found in Firestore for Contact:", clientContact);
          setYears([]);
        }
        setIsLoadingYears(false);
      },
      (error) => {
        console.error("❌ Firestore client listener error for Contact:", clientContact, error);
        setYears([]);
        setIsLoadingYears(false);
      }
    );

    return () => {
      console.log("🔄 Cleaning up real-time listener for Contact:", clientContact);
      unsubscribe();
    };
  }, [client]);

  // Initial load of years when component mounts
  useEffect(() => {
    if (client) {
      refreshYearsFromFirestore();
    }
  }, [client]);

  const showSuccessToast = (message) => {
    setToastMessage(message);
    setToastVariant("success");
    setShowToast(true);
  };

  const showErrorToast = (message) => {
    setToastMessage(message);
    setToastVariant("danger");
    setShowToast(true);
  };

  // Function to manually refresh years from Firestore
  const refreshYearsFromFirestore = async () => {
    try {
      setIsLoadingYears(true);
      const clientContact = client?.contact || client?.id;
      if (!clientContact) return;

      const clientDocRef = getClientDocRef(clientContact);
      const clientDoc = await firestoreHelpers.get(clientDocRef);

      if (clientDoc) {
        const clientYears = clientDoc.years || [];
        clientYears.sort((a, b) => parseInt(b) - parseInt(a));
        setYears(clientYears);
        console.log("🔄 Manually refreshed years from Firestore:", clientYears);
      }
    } catch (error) {
      console.error("❌ Error refreshing years:", error);
    } finally {
      setIsLoadingYears(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/clients');
  };

  const handleManageDocuments = (year) => {
    navigate('/admin/documents', {
      state: { client: currentClient, filterYear: year }
    });
  };

  const handleEditYear = (oldYear) => {
    setOriginalYear(oldYear);
    setEditYear(oldYear);
    setShowEditYearModal(true);
  };

  const handleEditYearSubmit = async () => {
    // Validate 4-digit year input
    if (!editYear || isNaN(editYear) || editYear.length !== 4) {
      showErrorToast("Please enter a valid 4-digit year");
      return;
    }

    const yearNum = parseInt(editYear);
    if (yearNum < 1900 || yearNum > 2100) {
      showErrorToast("Please enter a year between 1900 and 2100");
      return;
    }

    // Format to "2021-22" style
    const formattedNewYear = formatYear(editYear);

    // Check if the formatted year is same as original (no change needed)
    if (formattedNewYear === originalYear) {
      setShowEditYearModal(false);
      return;
    }

    setIsEditingYear(true);
    try {
      // Check if new year already exists
      if (years.includes(formattedNewYear)) {
        showErrorToast(`Year ${formattedNewYear} already exists for this client.`);
        setIsEditingYear(false);
        return;
      }

      const clientContact = currentClient?.contact || client?.contact || client?.id;
      if (!clientContact) {
        throw new Error("Client Contact is required to edit a year");
      }

      console.log("✏️ Editing year for client Contact:", clientContact, "from", originalYear, "to", formattedNewYear);

      // Get old year document and its documents
      const oldYearDocRef = getYearDocRef(clientContact, originalYear);
      const oldYearDocumentsRef = getYearDocumentsRef(clientContact, originalYear);

      if (!oldYearDocRef || !oldYearDocumentsRef) {
        throw new Error("Unable to get year references");
      }

      // Get old year data and documents
      const oldYearDoc = await firestoreHelpers.get(oldYearDocRef);
      const oldDocuments = await documentHelpers.getDocuments(oldYearDocumentsRef);

      if (oldYearDoc) {
        // Get new year document
        const newYearDocRef = getYearDocRef(clientContact, formattedNewYear);
        const newYearDocumentsRef = getYearDocumentsRef(clientContact, formattedNewYear);

        if (!newYearDocRef || !newYearDocumentsRef) {
          throw new Error("Unable to get new year references");
        }

        // Create new year document with updated year
        const newYearData = {
          ...oldYearDoc,
          year: formattedNewYear,
          updatedAt: new Date().toISOString()
        };

        await firestoreHelpers.set(newYearDocRef, newYearData);

        // Copy all documents to new year
        for (const doc of oldDocuments) {
          const updatedDocData = {
            ...doc,
            year: formattedNewYear,
            updatedAt: new Date().toISOString()
          };
          delete updatedDocData.id; // Remove old ID
          await documentHelpers.createDocument(newYearDocumentsRef, updatedDocData);
        }

        // Delete old year and its documents
        await firestoreHelpers.delete(oldYearDocRef);

        // Update client's years array
        const clientDocRef = getClientDocRef(clientContact);
        if (clientDocRef) {
          const currentUserData = currentClient || client;
          const existingYears = currentUserData?.years || [];

          const updatedYears = existingYears.map(y => y === originalYear ? formattedNewYear : y)
            .sort((a, b) => {
              // Custom sort for year ranges like "2028-29"
              const getStartYear = (yearStr) => {
                if (yearStr.includes('-')) {
                  return parseInt(yearStr.split('-')[0]);
                }
                return parseInt(yearStr);
              };
              return getStartYear(b) - getStartYear(a);
            });
          await firestoreHelpers.update(clientDocRef, { years: updatedYears });
          console.log("📅 Updated client's years array in Firestore:", updatedYears);
        }

        showSuccessToast(`Year updated from ${originalYear} to ${formattedNewYear} successfully!`);
        console.log("✅ Year updated successfully in Firestore");

        // Close modal and reset form
        setShowEditYearModal(false);
        setEditYear("");
        setOriginalYear("");

        // Refresh years from Firestore
        setTimeout(() => {
          refreshYearsFromFirestore();
        }, 1000);
      } else {
        console.log("ℹ️ No data found for the specified year to update");
        showErrorToast("No data found for the specified year.");
      }
    } catch (error) {
      console.error("❌ Error updating year:", error);
      showErrorToast(`Failed to update year: ${error.message}`);
    } finally {
      setIsEditingYear(false);
    }
  };

  const handleDeleteYear = async (year) => {
    // Get document count for this year before deletion
    const docCount = await getDocumentCount(year);

    // Set delete target and show custom modal
    setDeleteYearTarget(year);
    setDeleteDocCount(docCount);
    setShowDeleteModal(true);
  };

  const confirmDeleteYear = async () => {
    if (!deleteYearTarget) return;

    const year = deleteYearTarget;
    const docCount = deleteDocCount;

    setShowDeleteModal(false);

    try {
      const clientContact = currentClient?.contact || client?.contact || client?.id;
      if (!clientContact) {
        throw new Error("Client Contact is required to delete a year");
      }

      console.log("🗑️ Deleting year", year, "for client Contact:", clientContact);

      // 1. DELETE ALL DOCUMENTS IN THE SUBCOLLECTION MANUALLY
      // Firestore does not cascade delete subcollections, so we must do it ourselves.
      const documentsRef = getYearDocumentsRef(clientContact, year);
      if (documentsRef) {
        // querySnapshot returned from helper contains plain objects with ID
        const docsSnapshot = await firestoreHelpers.getCollection(documentsRef);
        console.log(`🗑️ Found ${docsSnapshot.length} documents to delete in year ${year}`);

        // Use a loop to delete each document reliably
        for (const docData of docsSnapshot) {
          // Create reference directly using the collection ref and the doc ID
          // We avoid using 'doc' variable name for the item to prevent shadowing the import
          const docToDeleteRef = doc(documentsRef, docData.id);
          await firestoreHelpers.delete(docToDeleteRef);
        }
        console.log("✅ All documents in subcollection deleted.");
      }

      // 2. Delete the year document from Firestore
      const yearDocRef = getYearDocRef(clientContact, year);
      if (!yearDocRef) {
        throw new Error("Unable to get year document reference");
      }

      await firestoreHelpers.delete(yearDocRef);
      console.log(`🗑️ Deleted year document: ${year}`);

      // Remove the year from client's years array
      const clientDocRef = getClientDocRef(clientContact);
      if (clientDocRef) {
        const currentUserData = currentClient || client;
        const existingYears = currentUserData?.years || [];

        if (existingYears.includes(year)) {
          const updatedYears = existingYears.filter(y => y !== year);
          await firestoreHelpers.update(clientDocRef, { years: updatedYears });
          console.log("📅 Updated client's years array in Firestore after deletion:", updatedYears);
        }
      }

      showSuccessToast(`Year ${year} deleted successfully! ${docCount} ${docCount === 1 ? 'document' : 'documents'} removed.`);
      console.log(`✅ Year ${year} deleted successfully from Firestore`);

      // Clear document count for deleted year
      setDocumentCounts(prev => {
        const next = { ...prev };
        delete next[year];
        return next;
      });

      // Refresh years from Firestore
      setTimeout(() => {
        refreshYearsFromFirestore();
      }, 1000);

      // Navigate back if no years left after deletion
      const remainingYears = years.filter(y => y !== year);
      if (remainingYears.length === 0) {
        setTimeout(() => {
          navigate('/admin/clients', {
            state: {
              message: `All years deleted for ${currentClient?.name || client?.name}`,
              type: 'success'
            }
          });
        }, 1500);
      }
    } catch (error) {
      console.error("❌ Error deleting year:", error);
      showErrorToast(`Failed to delete year: ${error.message}`);
    } finally {
      setDeleteYearTarget(null);
      setDeleteDocCount(0);
    }
  };

  const handleAddYear = async () => {
    if (newYear && !isNaN(newYear) && newYear.length === 4) {
      const year = parseInt(newYear);
      if (year >= 1900 && year <= 2100) {
        setIsAddingYear(true);
        try {
          // Format year as "2028-29" format
          const formattedYear = formatYear(newYear);

          // Check if year already exists
          if (years.includes(formattedYear)) {
            showErrorToast(`Year ${formattedYear} already exists for this client.`);
            return;
          }

          // Get the client Contact (use the client Contact as document ID)
          const clientContact = currentClient?.contact || client?.contact || client?.id;
          const clientName = currentClient?.name || client?.name;

          if (!clientContact) {
            throw new Error("Client Contact is required to add a year");
          }

          console.log("➕ Adding year for client:", clientName, "(Contact:", clientContact, ")");
          console.log("📅 Formatted year:", formattedYear);

          // Create year document in Firestore
          // Structure: {safeEmail}/user/clients/{clientContact}/years/{year}
          const yearData = {
            year: formattedYear,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            documentCount: 0,
            status: "active"
          };

          // Get year document reference using client Contact with formatted year
          const yearDocRef = getYearDocRef(clientContact, formattedYear);
          if (!yearDocRef) {
            throw new Error("Unable to get year document reference");
          }

          console.log("💾 Creating year document in Firestore:", yearData);

          // Save the year document to Firestore
          await firestoreHelpers.set(yearDocRef, yearData);

          // Update the client's years array using client Contact
          const clientDocRef = getClientDocRef(clientContact);
          if (clientDocRef) {
            const currentUserData = currentClient || client;
            const existingYears = currentUserData?.years || [];

            if (!existingYears.includes(formattedYear)) {
              const updatedYears = [...existingYears, formattedYear].sort((a, b) => {
                // Custom sort for year ranges like "2028-29"
                const getStartYear = (yearStr) => {
                  if (yearStr.includes('-')) {
                    return parseInt(yearStr.split('-')[0]);
                  }
                  return parseInt(yearStr);
                };
                return getStartYear(b) - getStartYear(a);
              });
              await firestoreHelpers.update(clientDocRef, { years: updatedYears });
              console.log("📅 Updated client's years array in Firestore for Contact:", clientContact, "Years:", updatedYears);
            }
          }

          showSuccessToast(`Year ${formattedYear} added successfully!`);
          console.log(`✅ Year ${formattedYear} added successfully to Firestore`);

          // Manually update local years state to ensure UI updates immediately
          const currentUserData = currentClient || client;
          const existingYears = currentUserData?.years || [];
          if (!existingYears.includes(formattedYear)) {
            const updatedYears = [...existingYears, formattedYear].sort((a, b) => {
              // Custom sort for year ranges like "2028-29"
              const getStartYear = (yearStr) => {
                if (yearStr.includes('-')) {
                  return parseInt(yearStr.split('-')[0]);
                }
                return parseInt(yearStr);
              };
              return getStartYear(b) - getStartYear(a);
            });
            setYears(updatedYears);

            // Explicitly set count to 0 for new year
            setDocumentCounts(prev => ({
              ...prev,
              [formattedYear]: 0
            }));

            console.log("🔄 Manually updated local years state:", updatedYears);
          }

          // Also refresh from Firestore to ensure data consistency
          setTimeout(() => {
            refreshYearsFromFirestore();
          }, 1000);

          // Close modal and reset form
          setShowAddYearModal(false);
          setNewYear("");

        } catch (error) {
          console.error("❌ Error adding year:", error);
          showErrorToast("Failed to add year. Please try again.");
        } finally {
          setIsAddingYear(false);
        }
      } else {
        showErrorToast("Please enter a year between 1900 and 2100");
        setIsAddingYear(false);
      }
    } else {
      showErrorToast("Please enter a valid 4-digit year");
      setIsAddingYear(false);
    }
  };

  // State to store document counts for each year
  const [documentCounts, setDocumentCounts] = useState({});

  // Function to get document count from Firestore for a specific year
  const getDocumentCount = async (year) => {
    try {
      const clientContact = currentClient?.contact || client?.contact || client?.id;
      if (!clientContact) return 0;

      const documentsRef = getYearDocumentsRef(clientContact, year);
      if (!documentsRef) return 0;

      const docs = await documentHelpers.getDocuments(documentsRef);

      // Filter out placeholder and empty documents
      const realDocuments = docs.filter(doc =>
        doc &&
        doc.fileName &&
        doc.fileName !== "placeholder.txt" &&
        (doc.docName || doc.name) &&
        !(doc.docName || doc.name).includes("Initial Setup") &&
        !(doc.docName || doc.name).includes("Year 20")
      );

      return realDocuments.length;
    } catch (error) {
      console.error("❌ Error getting document count for year", year, ":", error);
      return 0;
    }
  };

  // Load document counts for all years when years change
  useEffect(() => {
    const loadDocumentCounts = async () => {
      if (years.length > 0) {
        setIsLoadingDocumentCounts(true);
        const counts = {};
        for (const year of years) {
          counts[year] = await getDocumentCount(year);
        }
        setDocumentCounts(counts);
        console.log("📊 Document counts loaded:", counts);
        setIsLoadingDocumentCounts(false);
      } else {
        setIsLoadingDocumentCounts(false);
      }
    };

    loadDocumentCounts();
  }, [years, currentClient]);

  // Refresh document counts when component becomes visible (user returns from document management)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && years.length > 0) {
        console.log("📊 Page became visible, refreshing document counts...");
        setTimeout(async () => {
          const counts = {};
          for (const year of years) {
            counts[year] = await getDocumentCount(year);
          }
          setDocumentCounts(counts);
          console.log("📊 Document counts refreshed on visibility change:", counts);
        }, 500);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [years]);

  // Function to get cached document count (synchronous)
  const getCachedDocumentCount = (year) => {
    return documentCounts[year] || 0;
  };

  // Function to refresh document count for a specific year
  const refreshDocumentCount = async (year) => {
    const count = await getDocumentCount(year);
    setDocumentCounts(prev => ({
      ...prev,
      [year]: count
    }));
    console.log(`📊 Refreshed document count for year ${year}: ${count}`);
  };

  return (
    <div>
      {/* Wrap Year management into a single container */}
      <div style={{ background: 'white', borderRadius: 12, padding: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', marginBottom: 18 }}>
        <h3 className="mb-3">📅 Year Management - {client?.name}</h3>
        {/* Instructions */}
        <div className="alert alert-info mb-3" style={{
          borderRadius: '12px',
          border: 'none',
          background: 'linear-gradient(45deg, #e3f2fd, #f3e5f5)'
        }}>
          <div className="d-flex align-items-center">
            <div style={{ fontSize: '1.5rem', marginRight: '12px' }}>💡</div>
            <div>
              <strong>How to view documents:</strong> Click on the <strong>Year badge</strong>, <strong>Document count</strong>, or <strong>View button</strong> to see documents for that year.
            </div>
          </div>
        </div>

        {/* Client Info Card - Same as Client Management */}
        <Card className="mb-2 shadow-sm">
          <Card.Body className="py-2">
            <div className="d-flex justify-content-between align-items-center">
              <div className="small text-muted">
                <strong>Contact:</strong> {client?.contact} | <strong>Email:</strong> {client?.email} | <strong>PAN:</strong> {client?.pan}
              </div>
              <Button variant="outline-primary" size="sm" onClick={handleBack}>
                ← Back to Client Management
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Section Header - Same as Client Management */}
        <Card className="mb-3">
          <Card.Body className="py-2">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">Years</h5>
                <small className="text-muted">Manage years for {client?.name}</small>
              </div>
              <div className="d-flex gap-2">
                <Button variant="success" onClick={() => setShowAddYearModal(true)}>
                  ➕ Add New Year
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Years Table - Enhanced Structure */}
        <div className="table-responsive shadow-sm rounded">
          <Table hover className="mb-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
            <thead style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <tr>
                <th style={{
                  padding: '16px 20px',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  border: 'none',
                  letterSpacing: '0.5px',
                  textAlign: 'center'
                }}>
                  📅 Years
                </th>
                <th style={{
                  padding: '16px 20px',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  border: 'none',
                  letterSpacing: '0.5px',
                  textAlign: 'center'
                }}>
                  📄 Documents
                </th>
                <th style={{
                  padding: '16px 20px',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  border: 'none',
                  letterSpacing: '0.5px',
                  textAlign: 'center'
                }}>
                  ⚡ Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoadingYears ? (
                <tr>
                  <td colSpan="3" style={{
                    padding: '60px 20px',
                    textAlign: 'center',
                    border: 'none'
                  }}>
                    <div className="d-flex flex-column align-items-center">
                      <div className="spinner-border text-primary mb-3" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="h5 text-muted mb-2">Loading years...</div>
                      <div className="text-muted">Please wait while we fetch year data</div>
                    </div>
                  </td>
                </tr>
              ) : years.map((year, index) => {
                const docCount = getCachedDocumentCount(year);
                return (
                  <tr key={year} style={{
                    backgroundColor: index % 2 === 0 ? '#f8f9ff' : 'white',
                    transition: 'all 0.3s ease',
                    borderLeft: '4px solid transparent'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e3f2fd';
                      e.currentTarget.style.borderLeft = '4px solid #2196f3';
                      e.currentTarget.style.transform = 'translateX(2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#f8f9ff' : 'white';
                      e.currentTarget.style.borderLeft = '4px solid transparent';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}>
                    <td style={{
                      padding: '16px 20px',
                      textAlign: 'center',
                      border: 'none',
                      borderBottom: '1px solid #e9ecef'
                    }}>
                      <div className="d-flex align-items-center justify-content-center">
                        <Badge
                          style={{
                            background: 'linear-gradient(45deg, #667eea, #764ba2)',
                            fontSize: '1rem',
                            padding: '8px 16px',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                          onClick={() => handleManageDocuments(year)}
                          title={`Click to view ${docCount} documents for year ${formatYear(year)}`}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                          }}
                        >
                          {formatYear(year)}
                        </Badge>
                        {year === new Date().getFullYear().toString() && (
                          <div className="ms-2">
                            <small className="badge bg-success" style={{ fontSize: '0.7rem' }}>Current</small>
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{
                      padding: '16px 20px',
                      textAlign: 'center',
                      border: 'none',
                      borderBottom: '1px solid #e9ecef'
                    }}>
                      {isLoadingDocumentCounts ? (
                        <div className="d-flex align-items-center">
                          <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span className="text-muted">Loading...</span>
                        </div>
                      ) : (
                        <Badge
                          style={{
                            background: docCount > 0
                              ? 'linear-gradient(45deg, #17a2b8, #20c997)'
                              : 'linear-gradient(45deg, #6c757d, #495057)',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            padding: '8px 16px',
                            borderRadius: '12px',
                            boxShadow: docCount > 0
                              ? '0 2px 8px rgba(23, 162, 184, 0.3)'
                              : '0 2px 8px rgba(108, 117, 125, 0.3)',
                            transition: 'all 0.3s ease'
                          }}
                          onClick={() => handleManageDocuments(year)}
                          title="Click to manage documents"
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = docCount > 0
                              ? '0 4px 12px rgba(23, 162, 184, 0.4)'
                              : '0 4px 12px rgba(108, 117, 125, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = docCount > 0
                              ? '0 2px 8px rgba(23, 162, 184, 0.3)'
                              : '0 2px 8px rgba(108, 117, 125, 0.3)';
                          }}
                        >
                          📄 {docCount} {docCount === 1 ? 'Document' : 'Documents'}
                        </Badge>
                      )}
                    </td>
                    <td style={{
                      padding: '16px 20px',
                      textAlign: 'center',
                      border: 'none',
                      borderBottom: '1px solid #e9ecef'
                    }}>
                      <div className="d-flex gap-2 justify-content-center">
                        <button
                          onClick={() => handleManageDocuments(year)}
                          title="View documents for this year"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            background: '#eff6ff',
                            color: '#3b82f6',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 4px rgba(59, 130, 246, 0.1)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#3b82f6';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 6px rgba(59, 130, 246, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#eff6ff';
                            e.currentTarget.style.color = '#3b82f6';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.1)';
                          }}
                        >
                          <FiEye size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteYear(year)}
                          title="Delete this year and all its documents"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            background: '#fef2f2',
                            color: '#ef4444',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 4px rgba(239, 68, 68, 0.1)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#ef4444';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 6px rgba(239, 68, 68, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#fef2f2';
                            e.currentTarget.style.color = '#ef4444';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.1)';
                          }}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!isLoadingYears && years.length === 0 && (
                <tr>
                  <td colSpan="3" style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#6c757d',
                    fontSize: '1.1rem',
                    border: 'none'
                  }}>
                    <div>
                      <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📅</div>
                      <div style={{ fontWeight: '500', marginBottom: '8px' }}>No years found</div>
                      <div style={{ fontSize: '0.9rem', color: '#adb5bd' }}>Click "Add New Year" to get started</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Add New Year Modal */}
      <Modal show={showAddYearModal} onHide={() => setShowAddYearModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>📅 Add New Year</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label><strong>📅 Enter New Year</strong></Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g., 2022, 2023, 2027"
                value={newYear}
                onChange={(e) => setNewYear(e.target.value)}
                min="1900"
                max="2100"
                autoFocus
              />
              <Form.Text className="text-muted">
                Enter a 4-digit year (1900-2100)
              </Form.Text>
            </Form.Group>

            <div className="bg-light p-3 rounded mb-3">
              <h6 className="mb-2">👤 Client Information</h6>
              <div><strong>Name:</strong> {client?.name}</div>
              <div><strong>PAN:</strong> {client?.pan}</div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddYearModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddYear}
            disabled={!newYear || newYear.length !== 4 || isAddingYear}
          >
            {isAddingYear ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Adding...
              </>
            ) : (
              '➕ Add Year'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Year Modal */}
      <Modal show={showEditYearModal} onHide={() => setShowEditYearModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>✏️ Edit Year</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label><strong>📅 Edit Year</strong></Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g., 2022, 2023, 2027"
                value={editYear}
                onChange={(e) => setEditYear(e.target.value)}
                min="1900"
                max="2100"
                autoFocus
              />
              <Form.Text className="text-muted">
                Enter a 4-digit year (1900-2100)
              </Form.Text>
            </Form.Group>

            <div className="bg-light p-3 rounded mb-3">
              <h6 className="mb-2">👤 Client Information</h6>
              <div><strong>Name:</strong> {currentClient?.name || client?.name}</div>
              <div><strong>PAN:</strong> {currentClient?.pan || client?.pan}</div>
              <div><strong>Current Year:</strong> {originalYear}</div>
            </div>

            {editYear && editYear !== originalYear && (
              <div className="bg-warning bg-opacity-10 border border-warning rounded p-3 mb-3">
                <div className="d-flex align-items-center">
                  <div className="text-warning me-2">⚠️</div>
                  <div>
                    <strong>Year Change:</strong> {originalYear} → {editYear}
                    <br />
                    <small className="text-muted">All documents will be moved to the new year.</small>
                  </div>
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowEditYearModal(false);
              setEditYear("");
              setOriginalYear("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleEditYearSubmit}
            disabled={!editYear || editYear.length !== 4 || editYear === originalYear || isEditingYear}
          >
            {isEditingYear ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Updating...
              </>
            ) : (
              '✏️ Update Year'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={4000}
          autohide
          bg={toastVariant}
        >
          <Toast.Header>
            <strong className="me-auto">
              {toastVariant === "success" ? "✅ Success" : "❌ Error"}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Custom Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0',
          }}
          className="border-0"
        >
          <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600' }}>
            <FiTrash2 size={22} />
            <span>Confirm Deletion</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '30px', background: '#f8fafc' }}>
          <div style={{ textAlign: 'center', marginBottom: '25px' }}>
            <div style={{
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: '0 4px 12px rgba(251, 191, 36, 0.2)',
            }}>
              <FiTrash2 size={32} color="#f59e0b" />
            </div>
            <p style={{ color: '#64748b', fontWeight: '500', marginBottom: '8px', fontSize: '0.95rem' }}>
              Are you sure you want to delete year
            </p>
            <h4 style={{ color: '#1e293b', fontWeight: '700', marginBottom: '8px', fontSize: '1.3rem' }}>
              {deleteYearTarget}
            </h4>
            <p style={{ color: '#64748b', marginBottom: '0', fontSize: '0.9rem' }}>
              for {currentClient?.name || client?.name}?
            </p>
          </div>

          <div style={{
            borderRadius: '12px',
            background: 'white',
            border: '2px solid #e0e7ff',
            padding: '20px',
            marginBottom: '15px'
          }}>
            <div style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '12px', color: '#4f46e5', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.2rem' }}>📊</span>
              {deleteDocCount} {deleteDocCount === 1 ? 'document' : 'documents'} will be deleted
            </div>
            <div style={{ fontSize: '0.9rem', color: '#64748b', paddingLeft: '28px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FiFolder size={16} color="#6366f1" />
              <span>All documents in this year will be permanently removed</span>
            </div>
          </div>

          <div style={{
            borderRadius: '12px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            padding: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ fontSize: '1.2rem' }}>⚠️</span>
            <span style={{ color: '#991b1b', fontSize: '0.9rem', fontWeight: '500' }}>
              <strong>Warning:</strong> This action cannot be undone.
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ border: 'none', padding: '20px 30px', background: '#f8fafc' }}>
          <Button
            variant="light"
            onClick={() => setShowDeleteModal(false)}
            style={{
              borderRadius: '10px',
              padding: '10px 24px',
              fontWeight: '600',
              border: '2px solid #e5e7eb',
              background: 'white',
              color: '#64748b',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#cbd5e1';
              e.target.style.background = '#f8fafc';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.background = 'white';
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteYear}
            style={{
              borderRadius: '10px',
              padding: '10px 24px',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
              color: 'white',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
            }}
          >
            <FiTrash2 size={16} style={{ marginRight: '8px' }} />
            Delete Year
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default YearManagement;
